<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import Data from '@/api/data';
import StatusWidget from './StatusWidget.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  widgetTitle: {
    type: String,
    default: 'Preview'
  }
});

const emit = defineEmits(['update:modelValue']);

const config = ref({
  ...props.modelValue,
  mode: props.modelValue.mode ?? 'toggle',
  states: props.modelValue.states ?? []
});
const loadingData = ref(false);
const subcollections = ref({});
const validFields = ref([]);

onMounted(async () => {
  await getSubcollections();
  if (config.value.source) {
    updateValidFields(config.value.source);
  }
});

const getSubcollections = async () => {
  loadingData.value = true;
  try {
    const response = await Data.get(props.dataID);
    if (response.body.status === 'success') {
      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'messages'];
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details)
          .filter(([key]) => !ignoreSubcollections.includes(key))
          .sort(([a], [b]) => a.localeCompare(b))
      );
    }
  } catch (err) {
    console.error('Error fetching subcollections:', err);
  } finally {
    loadingData.value = false;
  }
};

const updateValidFields = (source) => {
  if (source && subcollections.value[source]) {
    validFields.value = Object.entries(subcollections.value[source])
      .filter(([, type]) => ['int', 'float', 'bool', 'NoneType', 'str'].includes(type))
      .map(([field]) => field)
      .sort((a, b) => a.localeCompare(b));
  } else {
    validFields.value = [];
  }
};

const handleSourceChange = (newSource) => {
  config.value.source = newSource;
  config.value.field = '';
  updateValidFields(newSource);
  emitUpdate();
};

const setMode = (mode) => {
  config.value.mode = mode;
  emitUpdate();
};

let stateIdCounter = Math.max(0, ...(props.modelValue.states?.map(s => s.id ?? 0) ?? [0]));

const addState = () => {
  config.value.states.push({ id: ++stateIdCounter, value: '', label: '', color: '#6b7280' });
  emitUpdate();
};

const removeState = (index) => {
  config.value.states.splice(index, 1);
  emitUpdate();
};

const emitUpdate = () => {
  emit('update:modelValue', config.value);
};

const previewStreamData = computed(() => {
  const isCustom = config.value.mode === 'custom';
  const previewValue = isCustom
    ? (config.value.states?.[0]?.value || null)
    : 1;
  return {
    __preview__: {
      lastValue: { __field__: previewValue },
      lastUpdate: null
    }
  };
});

const previewWidget = computed(() => ({
  ...config.value,
  title: props.widgetTitle || 'Preview',
  source: '__preview__',
  field: '__field__'
}));
</script>

<template>
  <div class="space-y-4">
    <!-- Source and Field -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Data Source *
          <span v-if="loadingData" class="inline-flex items-center ml-2" title="Loading options...">
            <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
          </span>
        </label>
        <select
          :value="config.source"
          @change="handleSourceChange($event.target.value)"
          :disabled="loadingData"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">Select a source...</option>
          <option v-for="source in Object.keys(subcollections)" :key="source" :value="source">
            {{ source }}
          </option>
        </select>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Field *
          <span v-if="!config.source" class="font-normal text-red-600 text-xs ml-1">(select source first)</span>
        </label>
        <select
          v-model="config.field"
          :disabled="!config.source || loadingData"
          @change="emitUpdate"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">Select a field...</option>
          <option v-for="field in validFields" :key="field" :value="field">
            {{ field }}
          </option>
        </select>
      </div>
    </div>

    <!-- Mode toggle -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mode</label>
      <div class="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden w-full">
        <button
          type="button"
          @click="setMode('toggle')"
          :class="[
            'flex-1 px-4 py-2 text-sm font-medium transition-colors',
            config.mode !== 'custom'
              ? 'bg-blue-600 text-white'
              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
          ]"
        >
          Toggle
        </button>
        <button
          type="button"
          @click="setMode('custom')"
          :class="[
            'flex-1 px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600',
            config.mode === 'custom'
              ? 'bg-blue-600 text-white'
              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
          ]"
        >
          Custom States
        </button>
      </div>
    </div>

    <!-- Toggle mode: On/Off labels -->
    <div v-if="config.mode !== 'custom'" class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          "On" Label
        </label>
        <input
          v-model="config.onLabel"
          type="text"
          placeholder="ON"
          @input="emitUpdate"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          "Off" Label
        </label>
        <input
          v-model="config.offLabel"
          type="text"
          placeholder="OFF"
          @input="emitUpdate"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>

    <!-- Custom states mode: states editor -->
    <div v-else class="space-y-2">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">States</label>

      <!-- Column headers -->
      <div v-if="config.states.length" class="grid grid-cols-[1fr_1fr_36px_24px] gap-2 px-1">
        <span class="text-xs text-gray-500 dark:text-gray-400">Value</span>
        <span class="text-xs text-gray-500 dark:text-gray-400">Label</span>
      </div>

      <!-- State rows -->
      <div
        v-for="(state, index) in config.states"
        :key="state.id ?? index"
        class="grid grid-cols-[1fr_1fr_36px_24px] gap-2 items-center"
      >
        <input
          v-model="state.value"
          type="text"
          placeholder="e.g. charging"
          @input="emitUpdate"
          class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <input
          v-model="state.label"
          type="text"
          placeholder="e.g. Charging"
          @input="emitUpdate"
          class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <!-- Color swatch — clicking the label opens the hidden color input -->
        <div class="relative w-9 h-8">
          <input
            :id="`state-color-${index}`"
            v-model="state.color"
            type="color"
            class="sr-only"
            @input="emitUpdate"
          />
          <label
            :for="`state-color-${index}`"
            :style="{ backgroundColor: state.color }"
            class="block w-full h-full rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer"
          ></label>
        </div>
        <button
          type="button"
          @click="removeState(index)"
          class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors text-lg leading-none"
          aria-label="Remove state"
        >
          ×
        </button>
      </div>

      <!-- Add state button -->
      <button
        type="button"
        @click="addState"
        class="w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
      >
        + Add state
      </button>
    </div>

    <!-- Preview -->
    <div>
      <h4 class="text-md font-semibold text-gray-800 dark:text-gray-200 mb-2">Preview</h4>
      <div class="bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-4 h-48 flex items-center justify-center">
        <StatusWidget
          :widget="previewWidget"
          :dataID="dataID"
          :streamData="previewStreamData"
        />
      </div>
      <p class="text-xs text-center text-gray-500 dark:text-gray-400 mt-1">
        <template v-if="config.mode === 'custom'">
          Preview shows first configured state.
        </template>
        <template v-else>
          Preview shows "on" state. Live data will appear once saved.
        </template>
      </p>
    </div>
  </div>
</template>
